import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, Download, Mail, RefreshCw, Printer, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResultPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const urlStatus = searchParams.get("status");

  useEffect(() => {
    async function fetch() {
      try {
        const { data } = await api.get(`/orders/${orderId}/status`);
        setOrder(data);
      } catch {
        setOrder({ payment_status: urlStatus || "failed", order_id: orderId });
      } finally {
        setLoading(false);
      }
    }
    fetch();
    // Poll for pending orders
    if (urlStatus === "pending" || urlStatus === "paid") {
      const interval = setInterval(async () => {
        try {
          const { data } = await api.get(`/orders/${orderId}/status`);
          setOrder(data);
          if (data.payment_status === "paid" || data.payment_status === "failed") {
            clearInterval(interval);
          }
        } catch (e) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Order poll failed:", e.message);
          }
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [orderId, urlStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral" />
      </div>
    );
  }

  const status = order?.payment_status || urlStatus || "failed";
  const downloadToken = order?.download_token;
  const downloadUrl = downloadToken ? `${API_URL}/api/download/${downloadToken}` : null;

  return (
    <div className="min-h-screen bg-cream">
      <main className="max-w-lg mx-auto px-4 py-12">
        {status === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-800">Your pack is ready!</h1>
              {order?.buyer_email && (
                <p className="text-slate-500 mt-2 font-body flex items-center justify-center gap-1.5">
                  <Mail className="w-4 h-4" /> We've also emailed it to <strong>{order.buyer_email}</strong>
                </p>
              )}
            </div>

            {downloadUrl && (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="download-pdf-btn">
                <Button className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-14 text-lg font-bold shadow-md shadow-coral/20">
                  <Download className="w-5 h-5 mr-2" />
                  Download {order?.pack_title || "PDF"}
                </Button>
              </a>
            )}

            <p className="text-xs text-slate-400 font-body">
              Download not starting? Open this page in Chrome or Safari, or use the link in your email.
            </p>

            <div className="bg-secondary rounded-2xl p-4 text-left space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <Printer className="w-4 h-4 text-coral" />
                <h3 className="font-heading font-semibold text-sm">Printing tips</h3>
              </div>
              <ul className="text-xs text-slate-600 space-y-1.5 list-disc list-inside font-body">
                <li>Print at 100% on A4 paper</li>
                <li>Card or laminate lasts longest</li>
                <li>For ages 3–5 an adult cuts the small pieces</li>
              </ul>
            </div>

            <Button variant="outline" className="rounded-full" onClick={() => navigate("/")}>
              Browse More Packs
            </Button>
          </motion.div>
        )}

        {status === "pending" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <Clock className="w-10 h-10 text-amber-600 animate-pulse" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Payment processing</h1>
              <p className="text-slate-500 mt-2 font-body">
                We'll email your PDF as soon as it's confirmed — usually within a few minutes.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking payment status...
            </div>
          </motion.div>
        )}

        {(status === "failed" || status === "cancelled") && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Payment didn't go through</h1>
              <p className="text-slate-500 mt-2 font-body">You haven't been charged.</p>
            </div>
            <Button
              onClick={() => navigate(-1)}
              className="bg-coral hover:bg-coral-hover text-white rounded-full h-12 px-8 font-bold"
              data-testid="try-again-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
